/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { AsyncData } from "types/api/common.type"
import { Attribute } from "types/models/item_attribute"
import {
  Menu,
  Category,
  Section,
  Item,
  SubDepartment,
  Department,
  ModifierGroup,
  ItemClass,
  OutletStore,
  SellingTime,
  MenuGroup,
  MenuGroupLinkedStore,
  GrabCategory,
  SuggestedDepartment,
  MerchantStoreInfo,
  OutletStoreV2,
  MerchantCity,
  MerchantBusinessType,
  MerchantDeliverOption,
  Translation,
} from "types/models/menu"
import { StoreMigrationMissingInfoSummary } from "types/api"
import { ItemFilterType } from "components/dropdowns/ItemPhotoSelect"
import { ServiceBasedMenuConfig } from "./global.type"

export interface MenuOverviewState {
  search?: string
  section?: Section | null
  category?: Category | null
  // item?: Item | null
  items: Item[]
  itemID?: string | null
  categories: Category[]
  department?: Department | null
  subDepartment?: SubDepartment | null
  departments: Department[]
  subDepartments: SubDepartment[]
  sellingTime: SellingTime | null
}

// V3 Multi-Select: New approach - explicit selections + exclude list
export interface BulkSelectionsMetadata {
  // Explicit selections (source of truth)
  // Value is the parent ID for tracking which parent the selection belongs to
  selectedDepartments?: { [departmentID: string]: boolean }
  selectedSubDepartments?: { [subDepartmentID: string]: string } // subDeptID -> departmentID
  selectedItems?: { [itemID: string]: { subDeptID: string; deptID: string } } // itemID -> parent IDs

  // Exclude list (explicitly unchecked despite parent selected)
  // Value is the parent IDs for tracking which parent the exclusion belongs to
  excludedSubDepartments?: { [subDepartmentID: string]: string } // subDeptID -> departmentID
  excludedItems?: { [itemID: string]: { subDeptID: string; deptID: string } } // itemID -> parent IDs

  // Total counts per parent (needed to determine if all children are excluded)
  // These are populated when subdepartments/items are loaded via RTK Query
  totalSubDepartmentsPerDepartment?: { [departmentID: string]: number }
  totalItemsPerSubDepartment?: { [subDepartmentID: string]: number }

  // Filter context (track which filter was active when bulk selecting)
  filterContext?: {
    itemFilter?: ItemFilterType
    timestamp: number
  }
}

export interface MenuDrawerState {
  section?: Section | null
  showSection?: boolean
  sellingTime?: SellingTime | null
  createdSellingTimeID?: string | null
  category?: Category | null
  showCategory?: boolean
  subDepartment?: SubDepartment | null
  showSubDepartment?: boolean
  item?: Item | null
  showItem?: boolean
  showSortCategories?: boolean
  showSortDepartments?: boolean
  showSortSubDepartments?: boolean
  showSortItems?: boolean
  showItemSelection?: boolean
  itemSelections?: {
    [itemID: string]: boolean
  }
  sourceShowItem?: string
  showLinkModifierGroup?: boolean
  linkedModifierGroupsDrawerState?: {
    linkedModifierGroups: ModifierGroup[]
    hasChanged: boolean
    hasBeenRearranged: boolean
  } | null
  itemSubDepartments?: {
    [departmentID: string]: ItemClass[]
  }
  showMenuGroup?: boolean
  showMenuGroupLinkedStore?: boolean
  showMenuGroupLinkStore?: boolean
  showSellingTimeEditor?: boolean
  sellingTimeTitle?: string
  showMultiSelectSellingTime?: boolean
  showDepartmentTaggingDrawer?: boolean
  showSubDepartmentTaggingDrawer?: boolean
  showUploadPhoto?: boolean
}

export interface MenuModalState {
  showDeleteMenu?: boolean
}

export interface BrandData extends AsyncData<Attribute[]> {
  forLocale?: string
  hasMore?: boolean
}

export interface MenuState {
  itemFilter: ItemFilterType
  isItemPhotoCoverageBannerSkipped: boolean
  merchantID: string
  merchantIDs: string[]
  merchantGroupID: string
  outlets?: MerchantStoreInfo[]
  outletsHasMore: boolean
  isFetchingOutlets: boolean
  isFetchingMoreOutlets: boolean
  outletsSearch?: MerchantStoreInfo[]
  migrationMissingInfoSummary?: StoreMigrationMissingInfoSummary
  outletsSearchHasMore: boolean
  isFetchingOutletsSearch: boolean
  isFetchingMoreOutletsSearch: boolean
  cityList?: MerchantCity[]
  totalItemsWithoutPhoto?: number
  isFetchingCityList: boolean
  merchantBusinessTypeList?: MerchantBusinessType[]
  isFetchingMerchantBusinessTypeList: boolean
  isFetchingMerchantAggregate: boolean
  merchantDeliverOptions?: MerchantDeliverOption[]
  menuGroups: MenuGroup[]
  menuGroupDetail: MenuGroup
  menuGroupLinkedStores?: MenuGroupLinkedStore[]
  menuGroupLinkedStoresIDs?: string[]
  menuGroupServiceBasedMenuConfig?: ServiceBasedMenuConfig
  outlet?: OutletStore | null
  outletV2?: OutletStoreV2 | null
  items: Item[]
  categories: Category[]
  departments: Department[]
  subDepartments: SubDepartment[]
  sellingTimes: SellingTime[]
  menu: Menu
  overview: MenuOverviewState
  drawer: MenuDrawerState
  modal: MenuModalState
  modifierGroupSearchKeyword: string
  isFetchingMenu: boolean
  isEditAllowed: boolean
  isStatusChangeAllowed: boolean
  isCreateItemAllowed: boolean
  isMartStore: boolean
  isMartDepartmentStructure: boolean
  isSellingTimeStructure: boolean
  isMenuGroup: boolean
  isUCS: boolean
  isMenuGroupServiceBasedMenuEnabled: boolean
  brands: BrandData
  suggestedDepartments: SuggestedDepartment[]
  isFetchingDataForGrabCategory?: boolean
  departmentsForGrabCategory?: ItemClass[]
  subDepartmentsForGrabCategory?: ItemClass[]
  parentName?: string
  selectedGrabCategory?: GrabCategory
  predictionIDGrabCategory?: string
  subDepartmentInDepartmentListForGrabCategory?: ItemClass[]
  isDepartmentTaggingMenuGroupEnabled?: boolean
  isEnableAttributes?: boolean
  isEnableFreeTextItemAttribute?: boolean
  isEnableDecimalItemAttribute?: boolean
  isMoreDepartments?: boolean
  isFetchingDepartments?: boolean
  hasMoreGrabCategory?: boolean
  offsetGrabCategory?: number
  alcoholPrediction?: boolean
  alcoholPredictionLoading?: boolean
  enableAlcoholSetting?: boolean
  enableBcrsMenuGroupV2?: boolean
  isMenuGroupPage?: boolean
  tobaccoPrediction?: boolean
  tobaccoPredictionLoading?: boolean
  enableTobaccoSetting?: boolean
  enableMenuTranslationPortal?: boolean
  stopAfterFirstPage?: boolean
  enablePartialMenu?: boolean
  enableGrabMexProImageEditor?: boolean
  autoTranslationQueue: Set<string>
  originalTranslationFromDSMap: Record<string, Translation>
  searchResults?: Item[]
  searchItemLoading: boolean
  searchQueryID?: string
  searchHighlightKey?: string
}
export interface SortableResource {
  resourceID: string
  sortOrder: number
}

export interface ItemPrediction {
  name: string
  desc: string
  photos: string[]
}

export enum EnhanceImageTask {
  BACKGROUND_REMOVAL = "BACKGROUND_REMOVAL",
  AUTO_BACKGROUND_REMOVAL = "AUTO_BACKGROUND_REMOVAL",
}
